<script setup>
/**
 * The queue's filter bar: type, stage, owner, sort, and the archived switch.
 *
 * Every control is a chip that reads as a sentence — "Stage is any" — and
 * every one of them only emits. The server is the sole filter authority, so
 * nothing here narrows a list; it names a query and hands it up.
 *
 * "Stage" filters by the system *category*, not by a workspace's individual
 * stage names: the four categories are the vocabulary every workflow maps to,
 * so one control works across every case type in the account.
 *
 * The owner and stage options carry their totals from `GET /counts`, which
 * reports the same scope the list is fetched with. One count vocabulary:
 * totals, never unread.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import {
  OWNER_SCOPE,
  SORT_OPTION,
  STAGE_CATEGORY,
  STAGE_CATEGORY_META,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // The store's filter set, verbatim.
  filters: {
    type: Object,
    required: true,
  },
  // `{ id, name }` case types the type chip offers.
  caseTypes: {
    type: Array,
    default: () => [],
  },
  // Queue totals from `GET /counts`, camelised.
  counts: {
    type: Object,
    default: () => ({}),
  },
  // True while any filter is narrowing the queue; shows the reset control.
  hasActiveFilters: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'reset']);

const { t } = useI18n();

const openMenu = ref(null);

const CATEGORY_ORDER = [
  STAGE_CATEGORY.SUBMITTED,
  STAGE_CATEGORY.IN_PROGRESS,
  STAGE_CATEGORY.WAITING,
  STAGE_CATEGORY.RESOLVED,
];

// The counts payload keys, camelised, for the facets that have a count.
const COUNT_KEY = {
  [OWNER_SCOPE.ALL]: 'all',
  [OWNER_SCOPE.MINE]: 'mine',
  [OWNER_SCOPE.UNASSIGNED]: 'unassigned',
  [STAGE_CATEGORY.SUBMITTED]: 'submitted',
  [STAGE_CATEGORY.IN_PROGRESS]: 'inProgress',
  [STAGE_CATEGORY.WAITING]: 'waiting',
  [STAGE_CATEGORY.RESOLVED]: 'resolved',
};

const countFor = value => {
  const count = props.counts[COUNT_KEY[value]];
  return Number.isFinite(count) ? count : null;
};

const typeOptions = computed(() => [
  {
    label: t('SUPPORT_CASES.FILTERS.TYPE.ANY'),
    value: null,
    icon: 'i-lucide-shapes',
  },
  ...props.caseTypes.map(caseType => ({
    label: caseType.name,
    value: caseType.id,
    icon: 'i-lucide-shapes',
  })),
]);

const categoryOptions = computed(() => [
  {
    label: t('SUPPORT_CASES.FILTERS.CATEGORY.ANY'),
    value: null,
    icon: 'i-lucide-circle',
  },
  ...CATEGORY_ORDER.map(category => ({
    label: t(STAGE_CATEGORY_META[category].labelKey),
    value: category,
    icon: STAGE_CATEGORY_META[category].icon,
    count: countFor(category),
  })),
]);

const ownerOptions = computed(() => [
  {
    label: t('SUPPORT_CASES.VIEWS.ALL'),
    value: OWNER_SCOPE.ALL,
    icon: 'i-lucide-users',
    count: countFor(OWNER_SCOPE.ALL),
  },
  {
    label: t('SUPPORT_CASES.VIEWS.MY_WORK'),
    value: OWNER_SCOPE.MINE,
    icon: 'i-lucide-user',
    count: countFor(OWNER_SCOPE.MINE),
  },
  {
    label: t('SUPPORT_CASES.VIEWS.UNASSIGNED'),
    value: OWNER_SCOPE.UNASSIGNED,
    icon: 'i-lucide-circle-dashed',
    count: countFor(OWNER_SCOPE.UNASSIGNED),
  },
]);

const sortOptions = computed(() => [
  {
    label: t('SUPPORT_CASES.FILTERS.SORT.LAST_ACTIVITY'),
    value: SORT_OPTION.LAST_ACTIVITY,
    icon: 'i-lucide-arrow-down-up',
  },
  {
    label: t('SUPPORT_CASES.FILTERS.SORT.CREATED_AT'),
    value: SORT_OPTION.CREATED_AT,
    icon: 'i-lucide-clock',
  },
]);

const archivedOptions = computed(() => [
  {
    label: t('SUPPORT_CASES.FILTERS.ARCHIVED.ACTIVE'),
    value: false,
    icon: 'i-lucide-inbox',
  },
  {
    label: t('SUPPORT_CASES.FILTERS.ARCHIVED.ARCHIVED'),
    value: true,
    icon: 'i-lucide-archive',
  },
]);

const MENUS = [
  { key: 'type', filterKey: 'caseTypeId', labelKey: 'TYPE.PREFIX' },
  { key: 'category', filterKey: 'category', labelKey: 'CATEGORY.PREFIX' },
  { key: 'owner', filterKey: 'owner', labelKey: 'OWNER.PREFIX' },
  { key: 'archived', filterKey: 'archived', labelKey: 'ARCHIVED.PREFIX' },
  { key: 'sort', filterKey: 'sortBy', labelKey: 'SORT.PREFIX' },
];

const optionsByKey = computed(() => ({
  type: typeOptions.value,
  category: categoryOptions.value,
  owner: ownerOptions.value,
  archived: archivedOptions.value,
  sort: sortOptions.value,
}));

const menus = computed(() =>
  MENUS.map(menu => {
    const active = props.filters[menu.filterKey] ?? null;
    const items = optionsByKey.value[menu.key].map(option => ({
      ...option,
      action: menu.key,
      isSelected: option.value === active,
    }));

    return {
      ...menu,
      items,
      prefix: t(`SUPPORT_CASES.FILTERS.${menu.labelKey}`),
      // A value this bundle has no option for is still a live filter (the
      // server decides), so the chip shows it rather than pretending it is off.
      selected: items.find(item => item.isSelected) || {
        label: String(active),
        icon: 'i-lucide-filter',
      },
    };
  })
);

const closeMenu = () => {
  openMenu.value = null;
};

const toggleMenu = key => {
  openMenu.value = openMenu.value === key ? null : key;
};

const onSelect = ({ action, value }) => {
  closeMenu();
  const menu = MENUS.find(entry => entry.key === action);
  emit('update', { [menu.filterKey]: value });
};
</script>

<template>
  <div
    v-on-click-outside="closeMenu"
    class="flex flex-wrap items-center gap-2"
    data-testid="case-filter-bar"
  >
    <div v-for="menu in menus" :key="menu.key" class="relative">
      <!-- The visible text is the accessible name ("Owner: All"), so the chip
           needs no label of its own — what it does need is to announce that it
           opens a menu, and whether that menu is open. -->
      <Button
        slate
        size="sm"
        :icon="menu.selected.icon"
        :class="{ 'bg-n-slate-9/10': openMenu === menu.key }"
        aria-haspopup="menu"
        :aria-expanded="openMenu === menu.key"
        :data-testid="`case-filter-${menu.key}`"
        @click="toggleMenu(menu.key)"
      >
        <span class="inline-flex items-center min-w-0 gap-1">
          <span class="shrink-0 text-n-slate-11">{{ menu.prefix }}</span>
          <span class="min-w-0 font-medium truncate text-n-slate-12">
            {{ menu.selected.label }}
          </span>
        </span>
        <Icon icon="i-lucide-chevron-down" class="shrink-0 size-4" />
      </Button>
      <DropdownMenu
        v-if="openMenu === menu.key"
        :menu-items="menu.items"
        class="min-w-52 top-full mt-2 ltr:left-0 rtl:right-0"
        :data-testid="`case-filter-menu-${menu.key}`"
        @action="onSelect"
      >
        <template #trailing-icon="{ item }">
          <span
            v-if="item.count !== null && item.count !== undefined"
            class="ltr:ml-auto rtl:mr-auto text-xs tabular-nums text-n-slate-11"
          >
            {{ item.count }}
          </span>
        </template>
      </DropdownMenu>
    </div>
    <Button
      v-if="hasActiveFilters"
      variant="link"
      color="slate"
      size="sm"
      :label="t('SUPPORT_CASES.FILTERS.CLEAR')"
      data-testid="case-filter-clear"
      @click="emit('reset')"
    />
  </div>
</template>
